import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { bool, func } from 'prop-types';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { userDisplayNameAsString } from '../../util/data';
import { pathByRouteName } from '../../util/routes';
import { selectIsAuthenticated } from '../../ducks/auth.duck';
import { logout } from '../../ducks/auth.duck';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { Avatar, Icons, NamedLink } from '../../components';
import { tabbablesIn } from '../Modal/Modal';

import css from './AccountSheet.module.css';

/**
 * Account bottom sheet (design canvas "Account Sheet", Option B): the phone/
 * tablet home for everything the ledger moved under Account — Inbox (with the
 * unread badge), Saved, Your listings, Account settings — topped by an
 * identity header. Signed out it keeps Saved (guest wishlist) and leads with
 * Log in. Same sheet vocabulary as the sort sheet and filter tray. Opened by
 * the bottom nav's Account tab and the tablet topbar's account icon.
 *
 * While open it is a dialog like the order windows (Modal.js): focus moves into it, Tab stays
 * inside it, Escape closes it, and focus goes back to the button that opened it.
 */
const AccountSheet = props => {
  const { isOpen, onClose } = props;
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const currentUser = useSelector(state => state.user.currentUser);
  // Sales and orders together, as on the topbar's account icon
  const notificationCount = useSelector(
    state =>
      (state.user.currentUserNotificationCount || 0) +
      (state.user.currentUserCustomerNotificationCount || 0)
  );
  const currentUserHasListings = useSelector(state => state.user.currentUserHasListings);
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const sheetRef = useRef(null);
  // The latest onClose, without re-running the effect below when its owner re-renders
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return undefined;
    const opener = document.activeElement;
    const sheet = sheetRef.current;
    if (sheet) sheet.focus();

    const handleKeyDown = e => {
      if (e.key === 'Escape') {
        onCloseRef.current();
      } else if (e.key === 'Tab' && sheet) {
        const tabbables = tabbablesIn(sheet);
        const active = document.activeElement;
        const isInside = sheet.contains(active);
        const first = tabbables[0];
        const last = tabbables[tabbables.length - 1];
        if (!first) {
          e.preventDefault();
        } else if (e.shiftKey && (!isInside || active === first || active === sheet)) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && (!isInside || active === last)) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Focus that went with the sheet goes back to where it was; focus moved on stays
      const active = document.activeElement;
      const focusIsLost = !active || active === document.body;
      if (focusIsLost && opener && opener !== document.body && document.body.contains(opener)) {
        opener.focus({ preventScroll: true });
      }
    };
  }, [isOpen]);

  if (!isOpen || typeof document === 'undefined') return null;

  const displayName = userDisplayNameAsString(currentUser, '');

  // Phone and tablet log in and sign up on their pages (the desktop topbar keeps the window),
  // and come back here afterwards
  const goToAuthPage = routeName => {
    onClose();
    const from = `${location.pathname}${location.search}${location.hash}`;
    history.push({ pathname: pathByRouteName(routeName, routeConfiguration), state: { from } });
  };

  const handleLogout = () => {
    onClose();
    dispatch(logout()).then(() => history.push('/'));
  };

  const row = (icon, labelId, linkProps, extras = {}) => (
    <NamedLink
      {...linkProps}
      className={classNames(css.row, { [css.rowTinted]: extras.tinted })}
      onClick={onClose}
    >
      <span className={css.rowIcon}>
        <Icons name={icon} />
      </span>
      <span className={css.rowLabel}>
        <FormattedMessage id={labelId} />
      </span>
      {extras.badge > 0 ? <span className={css.badge}>{extras.badge}</span> : null}
    </NamedLink>
  );

  return ReactDOM.createPortal(
    <>
      <div className={css.overlay} onClick={onClose} />
      <div
        ref={sheetRef}
        className={css.sheet}
        role="dialog"
        aria-modal="true"
        aria-label={intl.formatMessage({ id: 'AccountSheet.heading' })}
        tabIndex="-1"
      >
        <div className={css.handle} />

        {isAuthenticated ? (
          <>
            {/* Identity header — "View profile" folds the profile row away */}
            <NamedLink name="ProfileSettingsPage" className={css.identity} onClick={onClose}>
              <Avatar className={css.avatar} user={currentUser} disableProfileLink />
              <span className={css.identityText}>
                <span className={css.identityName}>{displayName}</span>
                <span className={css.identityHint}>
                  <FormattedMessage id="AccountSheet.viewProfile" />
                </span>
              </span>
            </NamedLink>

            <div className={css.rows}>
              {row(
                'accountInbox',
                'AccountSheet.inbox',
                {
                  name: 'InboxPage',
                  params: { tab: currentUserHasListings ? 'sales' : 'orders' },
                },
                { tinted: true, badge: notificationCount }
              )}
              {row('navFavourites', 'AccountSheet.saved', { name: 'FavoritesPage' })}
              {row('accountListings', 'AccountSheet.yourListings', { name: 'ManageListingsPage' })}
              {row('accountSettings', 'AccountSheet.accountSettings', {
                name: 'AccountSettingsPage',
              })}
            </div>

            <button type="button" className={css.logoutRow} onClick={handleLogout}>
              <span className={css.rowIcon}>
                <Icons name="accountLogout" />
              </span>
              <span className={css.rowLabel}>
                <FormattedMessage id="AccountSheet.logout" />
              </span>
            </button>
          </>
        ) : (
          <>
            <div className={css.heading}>
              <FormattedMessage id="AccountSheet.heading" />
            </div>
            <div className={css.rows}>
              {row('navFavourites', 'AccountSheet.saved', { name: 'FavoritesPage' })}
            </div>
            <div className={css.guestCopy}>
              <FormattedMessage id="AccountSheet.guestCopy" />
            </div>
            <div className={css.authActions}>
              <button
                type="button"
                className={css.loginButton}
                onClick={() => goToAuthPage('LoginPage')}
              >
                <FormattedMessage id="AccountSheet.login" />
              </button>
              <div className={css.signupLine}>
                <FormattedMessage id="AccountSheet.newHere" />{' '}
                <button
                  type="button"
                  className={css.signupLink}
                  onClick={() => goToAuthPage('SignupPage')}
                >
                  <FormattedMessage id="AccountSheet.signup" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </>,
    document.body
  );
};

AccountSheet.propTypes = {
  isOpen: bool.isRequired,
  onClose: func.isRequired,
};

export default AccountSheet;
